/**
 * Auth callback handlers + ProtectedRoute.
 *
 *   • <AuthCallback />         — legacy Emergent SSO hash-based callback
 *   • <AuthCallbackPage />     — current Google OAuth code-exchange callback
 *   • <ProtectedRoute>         — gate for authenticated dashboard routes
 */

import React, { useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../lib/constants';


// ── Legacy Emergent SSO callback (kept for backwards compatibility) ──
export function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: 'include',
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          navigate('/dashboard', { replace: true });
        } else {
          const errorData = await response.json().catch(() => ({}));
          toast.error(errorData.detail || `Authentication failed (${response.status})`);
          navigate('/login');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication error');
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="eyvo-loading">
      <div className="spinner" />
      <p>Authenticating...</p>
    </div>
  );
}


// ── Current Google OAuth callback ────────────────────────────────────
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      if (error) {
        toast.error(`Google login failed: ${error}`);
        navigate('/login');
        return;
      }

      if (!code) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            code,
            redirect_uri: window.location.origin + '/auth/callback',
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          await checkAuth();
          navigate('/dashboard', { replace: true });
        } else {
          const errorData = await response.json().catch(() => ({}));
          toast.error(errorData.detail || `Authentication failed (${response.status})`);
          navigate('/login');
        }
      } catch (err) {
        console.error('Google auth callback error:', err);
        toast.error('Authentication error');
        navigate('/login');
      }
    };

    processGoogleAuth();
  }, [navigate, setUser, checkAuth]);

  return (
    <div className="eyvo-loading">
      <div className="spinner" />
      <p>Authenticating with Google...</p>
    </div>
  );
}


// ── Route guard ──────────────────────────────────────────────────────
export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="eyvo-loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}
